class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isModalSearch = !!this.closest(".search-modal");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.hasTriedHigherLimit = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    // Removed the focusout event listener
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    // Add a new event listener to handle when the search modal is opened
    const searchModal = document.querySelector(".search-modal .modal__content");
    if (searchModal) {
      searchModal.addEventListener(
        "classChange",
        this.onModalClassChange.bind(this)
      );
    }

    document.addEventListener('locationChanged', () => {
      console.log('Location changed in predictive search, updating results...');
      this.cachedResults = {};
      this.hasTriedHigherLimit = false;

      if (this.isOpen && this.searchTerm) {
        this.getSearchResults(this.searchTerm);
      }
    });
  }

  /**
   * Safety backstop for when ProductCardDataManager isn't available to gate
   * pricing on the selected store. Cards must NEVER be revealed with an
   * unverified/base price - hide them instead (query/collection links inside
   * are unaffected). Logs an always-on (ungated) error so the missing script
   * is noticed.
   */
  hideCardsWithoutPriceManager(cards, context) {
    console.error(`[PredictiveSearch] ProductCardDataManager not available${context ? ` (${context})` : ''} - hiding product cards instead of showing unverified prices.`);
    Array.from(cards).forEach(card => {
      if (!card.parentNode) return;
      card.classList.add('location-unavailable');
      card.style.display = 'none';
      const submitButton = card.querySelector('.submit-button, .quick-add__submit, .product-form__submit');
      if (submitButton) submitButton.disabled = true;
    });
  }

  async updateLocationPrices() {
    // Get all current product cards in the search results
    const productCards = this.querySelectorAll('.product-item[data-product-handle]');
    if (productCards.length > 0) {
      console.log(`Updating ${productCards.length} predictive search results for new location...`);
      const remainingCount = await this.filterProductsByLocation(productCards);
      
      // If no products available at new location, show empty state
      if (remainingCount === 0) {
        return this.showEmptySearchState();
      }
      
      // Use ProductCardDataManager to re-process remaining cards with new location data
      if (window.productCardDataManager) {
        window.productCardDataManager.processProductCards(Array.from(productCards).filter(card => card.parentNode));
      } else {
        this.hideCardsWithoutPriceManager(productCards, 'updateLocationPrices');
      }
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.hasTriedHigherLimit = false; // Reset for new search
    
    // Clear cache for fresh search
    this.cachedResults = {};

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    ) {
      event.preventDefault();
    } else {
      // Close the modal when submitting to search page
      this.close();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  // The header search bar's click handler (theme-functions.js) opens the
  // search modal on every template except the cart, so the header's own
  // <predictive-search> instance must never fetch/render there. On the
  // /search template the header input is prefilled with search.terms, and
  // without this guard the focus that precedes the modal-opening click
  // kicks off a fetch whose results re-open the header dropdown UNDER the
  // modal (duplicate, unstyled results panel).
  searchModalOwnsSearchUI() {
    return (
      !this.isModalSearch &&
      document.querySelector(".search-modal.modal__content") !== null &&
      !document.body.classList.contains("template-cart")
    );
  }

  searchModalIsOpen() {
    const searchModal = document.querySelector(".search-modal.modal__content");
    return !!searchModal && searchModal.classList.contains("open_modal");
  }

  onFocus() {
    // Focusing the header bar is about to open the search modal instead;
    // don't start a header-level predictive fetch.
    if (this.searchModalOwnsSearchUI()) return;

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    if (!searchForTextElement) return;
    
    // Always update with the current search term
    searchForTextElement.innerText = newTerm;
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getCurrentLocation() {
    return window.locationManager?.currentLocation ||
      localStorage.getItem('selectedLocation') ||
      localStorage.getItem('currentPickingStore') ||
      '76';
  }

  getSearchUrl(searchTerm, limit = 50) {
    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', limit);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product,query,article,collection');
    // Fetch broadly from Shopify predictive search, then enforce location availability
    // client-side. Passing available_locations here narrows Shopify's ranked candidate
    // set too early, causing relevant terms to look sparse before product-card-data.js
    // can apply the store-specific price/availability rules.
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    // Predictive search does NOT match SKUs by default; list the default
    // product fields plus variants.sku so typing a SKU/PLU surfaces the product.
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');
    return url.toString();
  }

  getCacheKey(searchTerm) {
    return `${searchTerm.replace(/\s+/g, "-").toLowerCase()}::location-${this.getCurrentLocation()}`;
  }

  startRequest() {
    this.abortController.abort();
    this.abortController = new AbortController();
    return this.abortController.signal;
  }

  getSearchResults(searchTerm) {
    const queryKey = this.getCacheKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(this.getSearchUrl(searchTerm), { signal: this.startRequest() })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then(async (text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const predictiveSearchSection = doc.querySelector("#shopify-section-predictive-search");

        if (!predictiveSearchSection) {
          console.error("Predictive search section not found.");
          return this.fetchFallbackProducts(searchTerm); // Fetch fallback products if search results are not found
        }

        let productCards = Array.from(predictiveSearchSection.querySelectorAll(".product-item"));

        if (productCards.length === 0) {
          return this.fetchFallbackProducts(searchTerm); // Fetch fallback products if no matching products are found
        }

        console.log(`Showing ${productCards.length} search suggestions`);

        // Filter products by location availability FIRST
        const remainingCount = await this.filterProductsByLocation(productCards);
        
        // If no products available at location, trigger higher limit fetch or show empty state
        if (remainingCount === 0 && !this.hasTriedHigherLimit) {
          console.log('All search results filtered out by location, trying higher limit...');
          this.hasTriedHigherLimit = true;
          return this.getSearchResultsWithLimit(searchTerm, 100);
        }
        
        if (remainingCount === 0) {
          console.log('No products available at current location');
          return this.showEmptySearchState();
        }

        // Use ProductCardDataManager to process remaining cards
        if (window.productCardDataManager) {
          window.productCardDataManager.processProductCards(productCards);
        } else {
          this.hideCardsWithoutPriceManager(productCards, 'getSearchResults');
        }

        return this.checkCartAndUpdateUI(predictiveSearchSection).then(
          () => {
            this.cachedResults[queryKey] = predictiveSearchSection.innerHTML;
            return predictiveSearchSection;
          }
        );
      })
      .then(async (predictiveSearchSection) => {
        // Count visible products after filtering
        const visibleProducts = predictiveSearchSection ? 
          Array.from(predictiveSearchSection.querySelectorAll(".product-item")).filter(
            card => card.style.display !== 'none' && !card.classList.contains('hidden')
          ).length : 0;
        
        console.log(`Visible products after filtering: ${visibleProducts}`);
        
        // If all products were filtered out, try fetching with a higher limit
        if (visibleProducts === 0 && !this.hasTriedHigherLimit) {
          console.log('All products filtered out, fetching more with higher limit...');
          this.hasTriedHigherLimit = true;
          return this.getSearchResultsWithLimit(searchTerm, 100);
        }
        
        // If still no products after trying higher limit, show empty state
        if (visibleProducts === 0 && this.hasTriedHigherLimit) {
          console.log('No products found even after higher limit, showing empty state');
          return this.showEmptySearchState();
        }
        
        const updatedResultsMarkup = predictiveSearchSection
          ? predictiveSearchSection.innerHTML
          : "";
        this.renderSearchResults(updatedResultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  getSearchResultsWithLimit(searchTerm, limit = 100) {
    console.log(`Fetching search results with limit: ${limit}`);
    
    // Ensure loading attribute is set
    this.setAttribute("loading", true);
    
    // Show the loading state with inline styles for reliability
    const loadingState = this.querySelector(".predictive-search__loading-state");
    if (loadingState) {
      loadingState.setAttribute("aria-hidden", "false");
      loadingState.style.display = "flex";
      loadingState.style.justifyContent = "center";
      loadingState.style.padding = "1rem";
    }
    
    // Update loading text
    const loadingTextElement = this.querySelector(".predictive-search__loading-text");
    if (loadingTextElement) {
      loadingTextElement.textContent = "Searching deeper...";
    }
    
    // Animate progress bar for deeper search
    const progressFill = this.querySelector(".predictive-search__progress-fill");
    if (progressFill) {
      progressFill.style.width = "30%";
      setTimeout(() => {
        if (progressFill) progressFill.style.width = "60%";
      }, 300);
      setTimeout(() => {
        if (progressFill) progressFill.style.width = "90%";
      }, 600);
    }
    
    // Update screen reader text
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    if (this.statusElement) {
      this.setLiveRegionText("Searching deeper...");
    }

    fetch(this.getSearchUrl(searchTerm, limit), { signal: this.startRequest() })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then(async (text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const predictiveSearchSection = doc.querySelector("#shopify-section-predictive-search");

        if (!predictiveSearchSection) {
          console.error("Predictive search section not found.");
          return this.fetchFallbackProducts(searchTerm);
        }

        let productCards = Array.from(predictiveSearchSection.querySelectorAll(".product-item"));

        if (productCards.length === 0) {
          console.log('No products found even with higher limit');
          return this.showEmptySearchState();
        }

        console.log(`Fetched ${productCards.length} products with higher limit`);

        // Filter products by location availability FIRST
        const remainingCount = await this.filterProductsByLocation(productCards);
        
        if (remainingCount === 0) {
          console.log('Still no products available at current location after higher limit');
          return this.showEmptySearchState();
        }

        // Use ProductCardDataManager to process remaining cards
        if (window.productCardDataManager) {
          window.productCardDataManager.processProductCards(productCards);
        } else {
          this.hideCardsWithoutPriceManager(productCards, 'getSearchResultsWithLimit');
        }

        return this.checkCartAndUpdateUI(predictiveSearchSection).then(
          () => predictiveSearchSection
        );
      })
      .then((predictiveSearchSection) => {
        // Count visible products after filtering
        const visibleProducts = predictiveSearchSection ? 
          Array.from(predictiveSearchSection.querySelectorAll(".product-item")).filter(
            card => card.style.display !== 'none' && !card.classList.contains('hidden')
          ).length : 0;
        
        console.log(`Visible products after higher limit fetch: ${visibleProducts}`);
        
        if (visibleProducts === 0) {
          console.log('Still no products after higher limit, showing empty state');
          return this.showEmptySearchState();
        }
        
        const updatedResultsMarkup = predictiveSearchSection
          ? predictiveSearchSection.innerHTML
          : "";
        this.renderSearchResults(updatedResultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        console.error("Failed to fetch with higher limit:", error);
        this.close();
        throw error;
      });
  }

  fetchFallbackProducts(searchTerm) {
    // Fetch products from a specific collection as fallback
    return fetch(this.getSearchUrl(searchTerm), { signal: this.startRequest() })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then(async (text) => {
        const doc = new DOMParser().parseFromString(text, "text/html");
        const fallbackSection = doc.querySelector(
          "#shopify-section-predictive-search"
        );

        if (!fallbackSection) {
          console.error("Fallback predictive search section not found.");
          return;
        }

        const productCards = Array.from(fallbackSection.querySelectorAll(".product-item"));
        if (!productCards.length) {
          console.error("No fallback products found.");
          return;
        }

        // Filter by location availability before processing
        const remainingCount = await this.filterProductsByLocation(productCards);

        console.log(`Showing ${remainingCount} available fallback products`);

		// Use ProductCardDataManager to process cards with JSON data
		if (window.productCardDataManager) {
		  window.productCardDataManager.processProductCards(Array.from(productCards));
		} else {
		  this.hideCardsWithoutPriceManager(productCards, 'fetchFallbackProducts');
		}

        return this.checkCartAndUpdateUI(fallbackSection).then(
          () => {
            this.cachedResults[this.getCacheKey(searchTerm)] = fallbackSection.innerHTML;
            return fallbackSection;
          }
        );
      })
      .then((fallbackSection) => {
        const updatedResultsMarkup = fallbackSection
          ? fallbackSection.innerHTML
          : "";
        this.renderSearchResults(updatedResultsMarkup);
      })
      .catch((error) => {
        console.error("Failed to fetch fallback products", error);
        this.close();
        throw error;
      });
  }

  async checkCartAndUpdateUI(section) {
    console.log("Check items");
    try {
      const cart = await fetch("/cart.js").then((res) => res.json());
      let productCards = section.querySelectorAll(".product-item");
      
      console.log(productCards);
      
      // Count visible products after filtering (non-hidden cards)
      const visibleProductsAfterFilter = () => {
        return Array.from(section.querySelectorAll(".product-item")).filter(
          card => card.style.display !== 'none' && !card.classList.contains('hidden')
        ).length;
      };
      productCards.forEach((card) => {
        const variantId = card.getAttribute("data-variant-id");
        const cartItem = cart.items.find(
          (item) => `${item.variant_id}` === variantId
        );
        const quantityElement = card.querySelector(
          ".collection-quantity-selector"
        );
        console.log(quantityElement);
        if (cartItem) {
          const quantity = cartItem.quantity;
          console.log(quantity);
          card.querySelector(".quick-add__quantity").style.display = "flex";

          quantityElement.setAttribute("value", quantity);
          card.classList.add("in-cart");
        } else {
          card.classList.remove("in-cart");
        }
      });
      
      // Update Delivery Pass button states
      if (typeof window.updateDeliveryPassButtons === 'function') {
        window.updateDeliveryPassButtons();
      }
    } catch (error) {
      console.error("Failed to fetch cart or update UI", error);
    }
  }

  renderSearchResults(resultsMarkup) {
    // A fetch started before the search modal opened may resolve after it;
    // never render/re-open the header instance while the modal owns the UI.
    if (!this.isModalSearch && this.searchModalIsOpen()) {
      this.close();
      return;
    }

    // Complete progress bar animation
    const progressFill = this.querySelector(".predictive-search__progress-fill");
    if (progressFill) {
      progressFill.style.width = "100%";
    }
    
    // Hide loading state after a short delay (CSS will handle display via [loading] attribute removal)
    const loadingState = this.querySelector(".predictive-search__loading-state");
    if (loadingState) {
      setTimeout(() => {
        loadingState.setAttribute("aria-hidden", "true");
      }, 300); // Short delay to show 100%
    }
    
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    updatePredictiveSearchLocation();

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    } else {
      console.error("Live region count element not found");
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    if (this.statusElement) {
      this.setLiveRegionText(this.loadingText);
      this.setAttribute("loading", true);
    } else {
      console.error("Status element not found");
    }
    
    // Show the loading state with inline styles for reliability
    const loadingState = this.querySelector(".predictive-search__loading-state");
    if (loadingState) {
      loadingState.setAttribute("aria-hidden", "false");
      loadingState.style.display = "flex";
      loadingState.style.justifyContent = "center";
      loadingState.style.padding = "1rem";
    }
    
    // Reset and update loading text
    const loadingTextElement = this.querySelector(".predictive-search__loading-text");
    if (loadingTextElement) {
      loadingTextElement.textContent = "Loading...";
    }
    
    // Animate progress bar for initial load
    const progressFill = this.querySelector(".predictive-search__progress-fill");
    if (progressFill) {
      progressFill.style.width = "0%";
      setTimeout(() => {
        if (progressFill) progressFill.style.width = "40%";
      }, 100);
      setTimeout(() => {
        if (progressFill) progressFill.style.width = "70%";
      }, 300);
    }
  }

  setLiveRegionText(statusText) {
    if (this.statusElement) {
      this.statusElement.setAttribute("aria-hidden", "false");
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute("aria-hidden", "true");
      }, 1000);
    } else {
      console.error("Status element not found");
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // Never open the header dropdown while the search modal is open.
    if (!this.isModalSearch && this.searchModalIsOpen()) return;

    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
  }

  // New method to handle modal class change
  onModalClassChange() {
    const searchModal = document.querySelector(".search-modal .modal__content");
    if (searchModal && searchModal.classList.contains("open_modal")) {
      const isModalSearch = this.closest(".search-modal");
      if (isModalSearch && this.getQuery().length) {
        this.open();
      } else {
        this.close();
      }
    } else {
      this.close();
    }
  }

  showEmptySearchState() {
    const locationName = window.locationManager?.locations[window.locationManager?.currentLocation]?.name || 'your location';
    const locationId = window.locationManager?.currentLocation || '';
    
    // Build the full search URL with location
    const searchUrl = `/search?q=${encodeURIComponent(this.searchTerm)}&type=product${locationId ? `&filter.p.m.custom.available_locations=${locationId}` : ''}`;
    
    // Get popular search suggestions from Liquid-injected data
    let popularSuggestionsHTML = '';
    try {
      const suggestionsData = document.getElementById('popular-search-suggestions');
      
      if (suggestionsData && suggestionsData.textContent) {
        const data = JSON.parse(suggestionsData.textContent);
        
        if (data.suggestions && data.suggestions.length > 0) {
          const suggestionLinks = data.suggestions.map(suggestion => 
            `<a href="${suggestion.url}" class="popular-suggestion-link">${suggestion.title}</a>`
          ).join('');
          
          popularSuggestionsHTML = `
            <div class="popular-suggestions" style="margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid #e0e0e0;">
              <div style="display: flex; align-items: flex-start; gap: 1.5rem;">
                <div style="flex-shrink: 0; font-weight: 600; color: #333; min-width: 80px;">Popular</div>
                <div style="display: flex; flex-wrap: wrap; gap: 0.75rem; flex: 1;">
                  ${suggestionLinks}
                </div>
              </div>
            </div>
          `;
        }
      }
    } catch (error) {
      console.error('Error loading popular suggestions:', error);
    }
    
    const emptyMarkup = `
      <div class="predictive-search__no-results" style="padding: 2rem; text-align: center;">
        <h3 style="margin-bottom: 0.5rem; font-size: 1.5rem; font-weight 600; color: #333;">No products found</h3>
        <p style="margin-bottom: 1.5rem; color: #666; font-size: 1.2rem;">No products matching "<strong>${this.searchTerm}</strong>" available at ${locationName}.</p>
        <a href="${searchUrl}" class="search-all-button" style="display: inline-block; padding: 0.75rem 1.5rem; background: #4CAF50; color: white; text-decoration: none; border-radius: 4px; font-weight: 500; transition: background 0.2s ease;">
          View all search results →
        </a>
        ${popularSuggestionsHTML}
      </div>
      <style>
        .search-all-button:hover {
          background: #45a049;
        }
        .popular-suggestion-link {
          display: inline-block;
          padding: 0.5rem 1rem;
          background: #f5f5f5;
          border-radius: 20px;
          color: #333;
          text-decoration: none;
          font-size: 1.3rem;
          transition: all 0.2s ease;
        }
        .popular-suggestion-link:hover {
          background: #e0e0e0;
          color: #000;
        }
      </style>
    `;
    this.renderSearchResults(emptyMarkup);
  }

  async filterProductsByLocation(productCards) {
    if (!window.locationManager) {
      console.warn('LocationManager not available, skipping location filtering');
      return productCards.length;
    }

    const currentLocation = window.locationManager.currentLocation;
    console.log(`Filtering ${productCards.length} search results for location ${currentLocation}...`);

    const cardsToRemove = [];

    for (const card of productCards) {
      const productHandle = card.getAttribute('data-product-handle');
      
      if (!productHandle) {
        console.warn('Product card missing data-product-handle attribute');
        continue;
      }

      // STRICT CHECK: Check available_locations metafield
      const availableLocationsAttr = card.getAttribute('data-available-locations');
      
      console.log(`[Search Filter] ${productHandle}: available_locations attr = "${availableLocationsAttr}"`);
      
      // ALWAYS enforce the check - if missing, null, "null" string, empty string, or empty array - HIDE
      if (!availableLocationsAttr || availableLocationsAttr === '' || availableLocationsAttr === '[]' || availableLocationsAttr === 'null') {
        console.log(`[Search Filter] ${productHandle}: HIDING - available_locations is null/empty`);
        cardsToRemove.push(card);
        continue;
      }
      
      // Parse and check the array
      try {
        let availableLocations = JSON.parse(availableLocationsAttr);
        
        // Check if array is empty
        if (!Array.isArray(availableLocations) || availableLocations.length === 0) {
          console.log(`[Search Filter] ${productHandle}: HIDING - Empty array`);
          cardsToRemove.push(card);
          continue;
        }
        
        // Check if current location is in array
        const isAvailableAtLocation = availableLocations.includes(currentLocation) || 
                                     availableLocations.includes(String(currentLocation));
        
        if (!isAvailableAtLocation) {
          console.log(`[Search Filter] ${productHandle}: HIDING - Location ${currentLocation} not in array [${availableLocations.join(', ')}]`);
          cardsToRemove.push(card);
          continue;
        }
        
        console.log(`[Search Filter] ${productHandle}: ✓ KEEPING - Location ${currentLocation} found in array`);
      } catch (e) {
        console.warn(`[Search Filter] ${productHandle}: HIDING - Failed to parse available_locations:`, e);
        cardsToRemove.push(card);
        continue;
      }
      
      // Check location-specific price from JSON data attribute (instant, no GraphQL call)
      let locationPrice = null;
      try {
        const locationPricesJson = card.getAttribute('data-location-prices');
        if (locationPricesJson) {
          const locationPrices = JSON.parse(locationPricesJson);
          locationPrice = locationPrices[currentLocation];
        }
      } catch (e) {
        console.warn('Failed to parse location prices JSON:', e);
      }
      
      if (!locationPrice || locationPrice === '' || locationPrice === 'null') {
        console.log(`Product ${productHandle} not available at location ${currentLocation} (no price), removing from results`);
        cardsToRemove.push(card);
        continue;
      }

      // Check hideInState metafield from data attribute
      const hideInState = card.getAttribute('data-hideinstate');
      if (hideInState && hideInState !== '') {
        const hiddenStates = hideInState.split(',').map(s => s.trim());
        if (hiddenStates.includes(currentLocation)) {
          console.log(`Product ${productHandle} hidden at location ${currentLocation}, removing from results`);
          cardsToRemove.push(card);
          continue;
        }
      }

      // Update the displayed price with location-specific price
      const priceElement = card.querySelector('.from_price');
      if (priceElement && locationPrice) {
        priceElement.textContent = `$${parseFloat(locationPrice).toFixed(2)}`;
        console.log(`Updated ${productHandle} price to $${locationPrice}`);
      }

      console.log(`Product ${productHandle} available at location ${currentLocation}`);
    }

    // Remove unavailable products from the DOM
    cardsToRemove.forEach(card => card.remove());
    
    const remainingCount = productCards.length - cardsToRemove.length;
    console.log(`Filtered search results: ${remainingCount} of ${productCards.length} products available at location ${currentLocation}`);
    
    return remainingCount;
  }
}

customElements.define("predictive-search", PredictiveSearch);

function addslashes(str) {
  return (str + "").replace(/[\\"']/g, "\\$&").replace(/\u0000/g, "\\0");
}

function getActiveSearchLocation() {
  return window.locationManager?.currentLocation ||
    localStorage.getItem('selectedLocation') ||
    localStorage.getItem('currentPickingStore') ||
    '76';
}

// Rewrites any /search link to carry the active store's location filter.
// Leaves collection/page/article suggestion links untouched.
function appendLocationToSearchLink(link, currentLocation) {
  const href = link.getAttribute('href');
  if (!href || href === '#') return;
  try {
    const url = new URL(href, window.location.origin);
    const searchPath = (typeof routes !== 'undefined' && routes.search_url) || '/search';
    if (url.pathname !== searchPath) return;
    url.searchParams.set('filter.p.m.custom.available_locations', currentLocation);
    link.setAttribute('href', `${url.pathname}${url.search}`);
  } catch (e) {
    // Malformed href; leave as-is
  }
}

// Update predictive search "Search for" link with current location
function updatePredictiveSearchLocation() {
  const currentLocation = getActiveSearchLocation();

  // Every rendered "Search for" link (header dropdown AND search modal can
  // each have one; querySelector only fixed the first, leaving the other on
  // the hardcoded default store).
  document.querySelectorAll('.predictive-search__item--term[data-search-base-url]').forEach(searchLink => {
    const baseUrl = searchLink.getAttribute('data-search-base-url');
    if (baseUrl) {
      searchLink.href = `${baseUrl}&filter.p.m.custom.available_locations=${currentLocation}`;
    }
  });

  // Suggestion links ("Suggestions" list) point at /search with no location
  // filter at all - rewrite them too.
  document.querySelectorAll('predictive-search a.predictive-search__item[href]').forEach(link => {
    if (link.hasAttribute('data-search-base-url')) return;
    appendLocationToSearchLink(link, currentLocation);
  });

  document.querySelectorAll('form[role="search"]').forEach(form => {
    let locationInput = form.querySelector('input[name="filter.p.m.custom.available_locations"]');
    if (!locationInput) {
      locationInput = document.createElement('input');
      locationInput.type = 'hidden';
      locationInput.name = 'filter.p.m.custom.available_locations';
      form.appendChild(locationInput);
    }
    locationInput.value = currentLocation;
  });

  console.log(`Updated predictive search link with location: ${currentLocation}`);
}

// Update on page load (after locationManager is ready). Runs immediately if
// the DOM is already parsed - this script can load after DOMContentLoaded has
// fired, in which case a listener alone would never run and searches would
// submit without the store filter.
function initPredictiveSearchLocation() {
  if (window.locationManager) {
    updatePredictiveSearchLocation();
  } else {
    document.addEventListener('locationManagerReady', updatePredictiveSearchLocation);
    // Belt-and-braces: apply the localStorage-derived location now so the
    // hidden inputs exist even if the ready event never fires.
    updatePredictiveSearchLocation();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPredictiveSearchLocation);
} else {
  initPredictiveSearchLocation();
}

// Update whenever location changes
document.addEventListener('locationChanged', updatePredictiveSearchLocation);

// Intercept header search form submissions to add location filter
function interceptHeaderSearchSubmission() {
  const searchForms = document.querySelectorAll('predictive-search form, search-form form');
  
  searchForms.forEach(form => {
    // Remove any existing listener to avoid duplicates
    form.removeEventListener('submit', handleSearchSubmit, true);
    
    // Add listener in capture phase to catch it early
    form.addEventListener('submit', handleSearchSubmit, true);
    
    console.log('Added location filter interceptor to search form');
  });
}

function handleSearchSubmit(event) {
  const form = event.target;
  const searchInput = form.querySelector('input[type="search"]');
  
  if (!searchInput || !searchInput.value.trim()) return;
  
  // Get current location (locationManager, then localStorage fallbacks)
  const currentLocation = getActiveSearchLocation();
  
  // Build search URL with location filter
  const searchUrl = new URL('/search', window.location.origin);
  searchUrl.searchParams.set('q', searchInput.value.trim());
  searchUrl.searchParams.set('options[prefix]', 'last');
  searchUrl.searchParams.set('type', 'product,article,collection');
  searchUrl.searchParams.set('filter.p.m.custom.available_locations', currentLocation);
  
  // Prevent default and navigate
  event.preventDefault();
  event.stopPropagation();
  window.location.href = searchUrl.toString();
  
  console.log(`Header search redirecting to: ${searchUrl.toString()}`);
}

// Setup on page load and after a short delay (for dynamic content). Run
// immediately when the DOM is already parsed (script may execute after
// DOMContentLoaded, which previously left forms without the interceptor).
function setupSearchSubmitInterceptors() {
  interceptHeaderSearchSubmission();
  setTimeout(interceptHeaderSearchSubmission, 500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupSearchSubmitInterceptors);
} else {
  setupSearchSubmitInterceptors();
}

// Also update after search results render (since the link gets replaced)
const originalRenderSearchResults = PredictiveSearch.prototype.renderSearchResults;
PredictiveSearch.prototype.renderSearchResults = function(resultsMarkup) {
  originalRenderSearchResults.call(this, resultsMarkup);
  
  // Process product cards with ProductCardDataManager to show prices
  setTimeout(() => {
    const productCards = this.querySelectorAll('.product-item');
    if (productCards.length > 0) {
      if (window.productCardDataManager) {
        console.log(`Processing ${productCards.length} predictive search product cards...`);
        window.productCardDataManager.processProductCards(Array.from(productCards));
      } else {
        this.hideCardsWithoutPriceManager(productCards, 'renderSearchResults');
      }
    }
    
    // Update location link after results are rendered
    updatePredictiveSearchLocation();
  }, 0);
};

// Observe class changes on the modal
const targetNode = document.querySelector(".search-modal .modal__content");
const config = { attributes: true, attributeFilter: ["class"] };
const callback = function (mutationsList, observer) {
  for (const mutation of mutationsList) {
    if (mutation.type === "attributes" && mutation.attributeName === "class") {
      const event = new Event("classChange");
      mutation.target.dispatchEvent(event);
    }
  }
};
const observer = new MutationObserver(callback);
if (targetNode) {
  observer.observe(targetNode, config);
}